/**
 * The rows a payment plan is made of — one place that decides what the customer
 * is shown, so five renderers can disagree about styling but not about money.
 *
 * The deposit is the reason this exists. It is collected at acceptance out of
 * the same job total the stages divide up, and the server now resolves a
 * `remaining_balance` stage net of it. A renderer that lists only the stages
 * therefore prints `$750.00` above a `Total $1,000.00` that no longer adds up,
 * with the $250 due today missing entirely.
 *
 * That was fixed in the approval modal first and left broken in the estimate
 * document, the itemized document, the PDF and the office section — the same
 * "applied at some call sites and not all" shape this whole change keeps
 * tripping over. Hence a row model rather than a fourth copy of the markup.
 */

export interface PaymentPlanRowSource {
  stages: Array<{
    position: number;
    label: string;
    triggerDescription: string;
    amount: { kind: string; amount: number | null };
    autoCharge: boolean;
  }>;
  grandTotal: number;
  /** The deposit in money. Absent on payloads built before it was returned. */
  depositAmount?: number | null;
}

export interface PaymentPlanRow {
  key: string;
  label: string;
  detail: string;
  /** Resolved money, or null for a plan that never resolved its stages. */
  amount: number | null;
  /** A remaining-balance stage, for renderers that label it. */
  isRemaining: boolean;
  autoCharge: boolean;
  isDeposit: boolean;
}

/**
 * The deposit row first when there is one, then every stage in order.
 *
 * Together with `grandTotal` beneath them these reconcile: on a $1,000 job with
 * a $250 deposit and one remaining stage, $250 + $750 = $1,000.
 */
export function paymentPlanRows(plan: PaymentPlanRowSource): PaymentPlanRow[] {
  const rows: PaymentPlanRow[] = [];
  const deposit = Number(plan.depositAmount ?? 0);
  if (deposit > 0) {
    rows.push({
      key: 'deposit',
      label: 'Deposit',
      detail: 'Due today, on acceptance',
      amount: deposit,
      isRemaining: false,
      autoCharge: false,
      isDeposit: true,
    });
  }
  for (const stage of plan.stages ?? []) {
    rows.push({
      key: `stage-${stage.position}-${stage.label}`,
      label: stage.label,
      detail: stage.triggerDescription,
      amount: stage.amount?.amount ?? null,
      isRemaining: stage.amount?.kind === 'remaining_balance',
      autoCharge: Boolean(stage.autoCharge),
      isDeposit: false,
    });
  }
  return rows;
}
